// src/pages/MLEnhancements.js
import React from "react";
import "./MLEnhancements.css";

// Update these paths to match your saved figures
import accByGroup from "./b1.png";
import cmBestGroup from "./b1.png";

const MLEnhancements = () => {
  return (
    <main className="ml-page">
      {/* Intro */}
      <section className="ml-intro">
        <h1 className="ml-title">ML Enhancements & Feature Experiments</h1>
        <p className="ml-subtitle">
          After training our original binary classification model, we ran a set
          of experiments to see whether we could design a better feature set
          using five seasons of data (2020–2024). This page summarizes how we
          built new features, compared different feature groups, and what we
          learned about model behavior on a highly imbalanced goal vs
          no-goal problem.
        </p>
      </section>

      {/* Data + Engineering */}
      <section className="ml-section">
        <h2 className="section-title">Data & New Engineered Features</h2>

        <article className="ml-card">
          <h3 className="card-title">Five Seasons of On-Net Shots</h3>
          <p className="card-text">
            We combined play-by-play data from{" "}
            <strong>five NHL seasons (2020–2024)</strong>:
          </p>
          <pre className="metrics-block small">
{`Loaded 564,450 total rows from 5 seasons.
Filtered to on-net shots only: 564,450 → 396,972 rows.`}
          </pre>
          <p className="card-text">
            As before, we worked only with <strong>on-net shots</strong>, so
            every row represents a shot that either became a goal or was saved.
          </p>
        </article>

        <article className="ml-card">
          <h3 className="card-title">New Context Features</h3>
          <p className="card-text">
            On top of the geometry and fatigue features from the earlier model,
            we engineered new variables that normalize game state to the{" "}
            <strong>shooter&apos;s perspective</strong>:
          </p>
          <ul className="card-list">
            <li>
              <strong>theirGoaliePulled / ourGoaliePulled</strong>: empty-net
              status for the opponent vs our team.
            </li>
            <li>
              <strong>ourScore / theirScore</strong>: score state at the time of
              the shot, from the shooter&apos;s perspective.
            </li>
            <li>
              <strong>lastEventSameTeam</strong>: whether the last event was
              generated by the same team as the shooter.
            </li>
          </ul>
          <pre className="metrics-block small">
{`Created theirGoaliePulled / ourGoaliePulled.
Created ourScore / theirScore.
Created lastEventSameTeam indicator.`}
          </pre>
          <p className="card-text">
            These features were designed to capture game context (score effects,
            goalie pulled, team momentum) in a way that is consistent for both
            home and away shooters.
          </p>
        </article>
      </section>

      {/* Feature groups */}
      <section className="ml-section">
        <h2 className="section-title">Feature Groups We Tested</h2>

        <article className="ml-card">
          <h3 className="card-title">Five Feature Group Variants</h3>
          <p className="card-text">
            We defined several feature groups to see which combinations of
            geometry, score, last-event context, and fatigue signals would
            actually improve the model:
          </p>
          <ul className="card-list">
            <li>
              <strong>geometry</strong>: shot distance, angle, x/y, off-wing,
              shotType.
            </li>
            <li>
              <strong>geometry+score</strong>: geometry plus ourScore,
              theirScore, period, isHomeTeam.
            </li>
            <li>
              <strong>geometry+lastEvent</strong>: geometry plus last event
              timing, distance, lastEventShotDistance, lastEventShotAngle, and
              lastEventSameTeam.
            </li>
            <li>
              <strong>geom+score+lastEvent</strong>: combines both score state
              and last-event features.
            </li>
            <li>
              <strong>full_model</strong>: a richer set with geometry, score,
              last-event indicators, pulled-goalie flags, shooter TOI, rest
              difference, and defending team TOI.
            </li>
          </ul>
          <p className="card-text">
            For each group, we one-hot encoded categoricals, scaled numeric
            features, and trained a{" "}
            <strong>plain logistic regression (no class weighting)</strong> on a
            75/25 train–test split, keeping the real imbalanced goal rate.
          </p>
        </article>
      </section>

      {/* Results table */}
      <section className="ml-section">
        <h2 className="section-title">Results: Accuracy, Precision, Recall</h2>

        <article className="ml-card">
          <h3 className="card-title">Feature Group Comparison</h3>
          <p className="card-text">
            The table below is a condensed version of the results printed by the
            Python script:
          </p>
          <pre className="metrics-block small">
{`=== Feature Group Comparison (5-season data) ===

group                  accuracy   precision   recall    TP   FP    TN    FN
full_model             0.9049      0.9911    0.0560   560    5  89,247  9,431
geometry               0.8993      0.0000    0.0000     0    0  89,252  9,991
geometry+score         0.8993      0.0000    0.0000     0    0  89,252  9,991
geometry+lastEvent     0.8993      0.0000    0.0000     0    0  89,252  9,991
geom+score+lastEvent   0.8993      0.0000    0.0000     0    1  89,251  9,991`}
          </pre>
          <p className="card-text">
            Four of the five feature groups collapse into a trivial classifier
            that <strong>always predicts “no goal”</strong>. This gives them
            nearly 0.90 accuracy simply because goals are rare, but precision
            and recall for goals are both zero.
          </p>
          <p className="card-text">
            The <strong>full_model</strong> is the only group that actually
            predicts some goals: it achieves extremely high precision (≈0.99)
            but very low recall (≈0.06). It identifies a small subset of very
            obvious goals and misses the vast majority of them.
          </p>
        </article>
{/* 
        <article className="ml-card">
          <h3 className="card-title">Accuracy by Feature Group</h3>
          <p className="card-text">
            We also visualized accuracy for each feature group to show how
            little it actually changes once the model learns to always predict
            &quot;no goal&quot; on this imbalanced dataset.
          </p>
          <div className="ml-graph">
            <img
              src={accByGroup}
              alt="Accuracy by feature group"
              className="graph-image"
            />
          </div>
          <p className="card-text">
            All models cluster around 0.90 accuracy, which looks impressive at
            first but is misleading: a model that never predicts a goal also
            achieves about 90% accuracy in the NHL because most on-net shots do
            not score.
          </p>
        </article> */}
      </section>

      {/* Confusion matrix for best group */}
      <section className="ml-section">
        <h2 className="section-title">Confusion Matrix for the Full Model</h2>

        <article className="ml-card">
          {/* <h3 className="card-title">Full Model (Unbalanced Logistic Regression)</h3>
          <p className="card-text">
            Among the five feature groups, <strong>full_model</strong> had the
            best combination of accuracy and non-zero goal predictions. Its
            confusion matrix looks like this:
          </p>

          <div className="ml-graph">
            <img
              src={cmBestGroup}
              alt="Confusion matrix for full_model feature group"
              className="graph-image"
            />
          </div> */}

          <pre className="metrics-block small">
{`Best group: full_model
Accuracy  = 0.905
Precision = 0.991
Recall    = 0.056

TN = 89,247   FP = 5
FN =  9,431   TP = 560`}
          </pre>

          <p className="card-text">
            The model almost never predicts &quot;goal&quot;, but when it does,
            it is almost always correct. That is why precision is so high and
            recall is so low. It has essentially become a{" "}
            <strong>very conservative danger detector</strong> that only fires
            on the most obvious goal situations.
          </p>
        </article>
      </section>

      {/* Comparison to original model */}
      <section className="ml-section">
        <h2 className="section-title">Comparison to the Original Model</h2>

        <article className="ml-card">
          <h3 className="card-title">Original 1-Season Balanced Model</h3>
          <p className="card-text">
            For reference, our original single-season model used{" "}
            <code>class_weight='balanced'</code> and a smaller feature set. Its
            performance on the test set was:
          </p>
          <pre className="metrics-block small">
{`Original 1-Season Logistic Regression

TN = 11,750   FP = 5,866
FN =   605   TP = 1,502

Accuracy  = 0.672
Precision = 0.204  (goals)
Recall    = 0.713  (goals)`}
          </pre>
          <p className="card-text">
            While overall accuracy was lower (~0.67), the model captured{" "}
            <strong>about 71% of all goals</strong> and was willing to predict a
            goal even if it meant some false positives. This behavior is much
            closer to what we want from an xG-style model.
          </p>
        </article>

        <article className="ml-card">
          <h3 className="card-title">What We Learned</h3>
          <ul className="card-list">
            <li>
              <strong>Accuracy alone is misleading.</strong> On a heavily
              imbalanced dataset, a model that never predicts goals can still
              reach ~90% accuracy, but is useless for actually finding goals.
            </li>
            <li>
              <strong>Unbalanced logistic regression collapses.</strong> With
              five seasons and no class weighting or resampling, the model
              prefers the safe &quot;no goal&quot; prediction almost all the
              time.
            </li>
            <li>
              <strong>Context features are powerful but not enough.</strong> The
              full_model uses pulled-goalie, score, last event, and fatigue
              variables to identify some extreme goal situations, but still
              misses most goals.
            </li>
            <li>
              <strong>The original balanced model behaved better.</strong> It
              had lower accuracy but much higher recall, making it far more
              useful as a probability-of-scoring model.
            </li>
          </ul>
        </article>
      </section>

      {/* Next steps */}
      <section className="ml-section">
        <h2 className="section-title">Next Steps for Improving the Model</h2>

        <article className="ml-card">
          <p className="card-text">
            Based on these experiments, our next ML enhancements will focus on:
          </p>
          <ul className="card-list">
            <li>
              Re-introducing <strong>class balancing</strong> (via{" "}
              <code>class_weight</code> or downsampling non-goals) so the model
              is not rewarded for always predicting &quot;no goal&quot;.
            </li>
            <li>
              Keeping a <strong>leaner, high-signal feature set</strong> based
              on geometry, score state, pulled-goalie status, last-event
              danger, and key fatigue metrics.
            </li>
            <li>
              I am going to create a mark 2 version of this, that substitutes possible better features into original data set that didnt always guess no goal.
            </li>
          </ul>
          <p className="card-text">
            In short, these experiments show that <strong>more features and more
            data are not automatically better</strong>. To build a strong xG
            model, we need to balance the classes, choose genuinely predictive
            features, and use evaluation metrics that reward correctly
            identifying goals, not just &quot;being safe&quot; and guessing no goal always.
          </p>
        </article>
      </section>
      {/* =========================== */}
{/*   ML ENHANCEMENTS – PART 2  */}
{/* =========================== */}

<section className="ml-section">
  <h2 className="section-title">
    ML Enhancements – Part 2: Gen1–Gen5 Feature Sets (Unbalanced Logistic Regression)
  </h2>

  <article className="ml-card">
    <h3 className="card-title">Setup: Five Seasons, Shooter-Perspective Features</h3>
    <p className="card-text">
      In this second round of experiments, we kept the same five seasons of data
      but changed how we engineered features and how we trained the model.
      Unlike the original model, <strong>we did not use class balancing</strong> here
      (no <code>class_weight='balanced'</code>). Instead, we trained a plain logistic
      regression on the real, highly imbalanced goal vs no-goal distribution.
    </p>

    <pre className="metrics-block small">
{`Loaded 564,450 total rows from 5 seasons.
Filtered to on-net shots only: 564,450 → 396,972 rows.

Created: theirGoaliePulled, ourGoaliePulled
Created: ourScore, theirScore
Created: lastEventSameTeam
Created: ourSkaters, theirSkaters, skaterDifferential`}
    </pre>

    <p className="card-text">
      All game-state variables were normalized to the shooter&apos;s perspective
      (ourScore vs theirScore, ourSkaters vs theirSkaters, theirGoaliePulled vs
      ourGoaliePulled) so the model sees situations consistently for home and
      away shooters.
    </p>
  </article>

  <article className="ml-card">
    <h3 className="card-title">Gen1 → Gen5: Five Generations of Feature Sets</h3>
    <p className="card-text">
      We then defined five feature sets (Gen1–Gen5) and trained a separate
      unbalanced logistic regression model on each:
    </p>
    <ul className="card-list">
      <li>
        <strong>gen1_original</strong> – our original 5-season feature set
        (geometry + shot context + fatigue + rest + pulled-goalie).
      </li>
      <li>
        <strong>gen2</strong> – swaps out weaker variables and adds last-event
        danger features (lastEventShotDistance, lastEventShotAngle,
        lastEventSameTeam).
      </li>
      <li>
        <strong>gen3</strong> – focuses on skater differential and game-state
        strength (ourSkaters, theirSkaters, skaterDifferential, location).
      </li>
      <li>
        <strong>gen4_</strong> – emphasizes score effects (ourScore, theirScore)
        along with key geometry and fatigue features.
      </li>
      <li>
        <strong>gen5</strong> – a cleaned-up, high-signal xG-style set that keeps
        only the strongest geometry, game state, fatigue, and last-event
        variables.
      </li>
    </ul>
    <p className="card-text">
      Each model uses one-hot encoding for categoricals, standardization for
      numeric features, and a plain unweighted logistic regression on the full
      class-imbalanced data.
    </p>
  </article>

  {/* GEN COMPARISON TABLE */}
  <article className="ml-card">
    <h3 className="card-title">Gen1 vs Gen2–Gen5: Accuracy, Precision, Recall & AUC</h3>
    <p className="card-text">
      The table below shows how each generation performed on the same test set
      (no class balancing). We report accuracy, precision/recall for goals, and
      ROC AUC:
    </p>

    <pre className="metrics-block small">
{`=== GEN1 vs GEN2/3/4/5 Feature Group Comparison ===

        group     n_raw   n_model   accuracy  precision  recall    auc
        gen5        18       25     0.6366     0.1843    0.7619  0.7540
        gen3        18       25     0.6357     0.1840    0.7624  0.7533
gen1_original        19       25     0.6411     0.1851    0.7541  0.7530
        gen4_       17       23     0.6376     0.1843    0.7586  0.7523
        gen2        18       24     0.6375     0.1839    0.7569  0.7521`}
    </pre>

    <p className="card-text">
      All five generations sit in a very tight band:
      <strong> accuracy ≈ 0.636–0.641, precision ≈ 0.184, recall ≈ 0.75–0.76,
      AUC ≈ 0.752–0.754</strong>. Because accuracy barely changes, we used{" "}
      <strong>ROC AUC</strong> to choose the best model.
    </p>
    <p className="card-text">
      By AUC, <strong>Gen5 is the best group</strong> (AUC ≈ 0.754), slightly
      ahead of Gen3 and Gen1. That means Gen5 gives the best ranking of shots
      from least dangerous to most dangerous, even though raw accuracy is
      similar across all models.
    </p>

    <pre className="metrics-block small">
{`Best group by AUC: gen5

Classification report for gen5:

Class 0 (no goal):
  precision = 0.959  recall = 0.623  f1 = 0.755  support = 89,252

Class 1 (goal):
  precision = 0.184  recall = 0.762  f1 = 0.297  support = 9,991

Overall accuracy = 0.637
Macro avg recall ≈ 0.692, macro avg precision ≈ 0.572`}
    </pre>

    <p className="card-text">
      Even without class weighting, Gen5 still achieves{" "}
      <strong>high recall for goals (~76%)</strong>, at the cost of low
      precision (~18%). This is acceptable for an xG-style model, where we care
      more about capturing dangerous chances than being perfectly precise on
      every single predicted goal.
    </p>
  </article>

  {/* COEFFICIENT PATTERNS */}
  <article className="ml-card">
    <h3 className="card-title">What the Coefficients Tell Us (Gen1–Gen5)</h3>
    <p className="card-text">
      We also looked at the top logistic regression coefficients by absolute
      value for every generation. The patterns are remarkably consistent:
    </p>

    <pre className="metrics-block small">
{`Across all generations (Gen1–Gen5):

- arenaAdjustedShotDistance has the largest negative weight
- xCordAdjusted and shotAngleAdjusted also have strong negative weights
- theirGoaliePulled consistently has a large positive weight
- shotType_SLAP and shotType_SNAP are strongly positive
- defendingTeamAverageTimeOnIceOfForwardsSinceFaceoff is positive
- averageRestDifference is positive
- fatigue variables (defending TOI, rest) stay important`}
    </pre>

    <p className="card-text">
      As we move into Gen3 and Gen5, new game-state variables show up with
      intuitive directions:
    </p>

    <pre className="metrics-block small">
{`Gen3 & Gen5 additions:

- skaterDifferential is positive (PP situations increase goal odds)
- location_Neu. Zone is negative (shots from neutral zone almost never score)
- theirSkaters has a negative weight (more defenders → lower goal odds)
- shooterTimeOnIceSinceFaceoff is negative (tired shooters are less likely to score)`}
    </pre>

    <p className="card-text">
      The sign and magnitude of these weights line up with hockey intuition:
      closer, better-angle shots from advantaged game states (PP, pulled goalie,
      tired defenders) are more likely to score. Long-distance, bad-angle,
      neutral-zone shots, or shots taken by tired shooters are less likely to
      score.
    </p>
  </article>

  {/* INTERPRETATION VS ORIGINAL MODEL */}
  <article className="ml-card">
    <h3 className="card-title">How This Compares to the Original Balanced Model</h3>
    <p className="card-text">
      The original single-season model used{" "}
      <code>class_weight='balanced'</code> and achieved:
    </p>
    <pre className="metrics-block small">
{`Original 1-season (balanced):

Accuracy  ≈ 0.672
Precision ≈ 0.204 (goals)
Recall    ≈ 0.713 (goals)`}
    </pre>

    <p className="card-text">
      In Part 2, all Gen1–Gen5 models were trained <strong>without</strong> class
      weighting on the full 5-season dataset. Despite that, Gen5 still reaches:
    </p>
    <pre className="metrics-block small">
{`Gen5 (5 seasons, unbalanced):

Accuracy  ≈ 0.637
Precision ≈ 0.184 (goals)
Recall    ≈ 0.762 (goals)
AUC       ≈ 0.754`}
    </pre>

    <p className="card-text">
      So compared to the original balanced 1-season model:
    </p>
    <ul className="card-list">
      <li>
        Accuracy is slightly lower (0.672 → ~0.637) because we removed class
        weighting and use a larger, harder dataset.
      </li>
      <li>
        Recall for goals is actually <strong>higher</strong> (~0.71 → ~0.76),
        even without balancing.
      </li>
      <li>
        ROC AUC is strong (~0.75), showing that the model ranks shot danger well
        across the full 5-season sample.
      </li>
    </ul>
    <p className="card-text">
      Overall, these experiments show that:
      <strong> good feature engineering (Gen5) plus a larger dataset can still produce a strong
      xG-style model even without class weighting</strong>, as long as we evaluate it with
      the right metrics (recall, AUC) and not just raw accuracy.
    </p>
  </article>
</section>

    </main>
  );
};

export default MLEnhancements;
